import React, { Component } from 'react'
import { Link } from 'react-router-dom'
import styles from './Header.module.scss'

interface MenuItem {
  id: string;
  label: string;
  href: string;
  icon: string;
}

interface HeaderState {
  isScrolled: boolean;
  isMenuOpen: boolean;
  activeMenu: string;
}

const menuItems: MenuItem[] = [
  { id: 'home', label: 'Home', href: '#home', icon: '🏠' },
  { id: 'about', label: 'About', href: '#about', icon: 'ℹ️' },
  { id: 'games', label: 'Games', href: '#games', icon: '🎱' },
  { id: 'tournaments', label: 'Tournaments', href: '#tournaments', icon: '🏆' },
  { id: 'tables', label: 'Tables', href: '#tables', icon: '🎯' },
  { id: 'contact', label: 'Contact', href: '#contact', icon: '✉️' }
]

export class Header extends Component<{}, HeaderState> {
  private handleScroll: (() => void) | null = null;
  private handleResize: (() => void) | null = null;
  private handleKeyDown: ((e: KeyboardEvent) => void) | null = null;

  constructor(props: {}) {
    super(props);
    this.state = {
      isScrolled: false,
      isMenuOpen: false,
      activeMenu: 'home'
    };
  }

  componentDidMount() {
    this.handleScroll = () => {
      const scrolled = window.scrollY > 20;
      if (scrolled !== this.state.isScrolled) {
        this.setState({ isScrolled: scrolled });
      }
    };

    this.handleResize = () => {
      if (window.innerWidth > 1199 && this.state.isMenuOpen) {
        this.setState({ isMenuOpen: false });
      }
    };

    this.handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && this.state.isMenuOpen) {
        this.setState({ isMenuOpen: false });
      }
    };

    window.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.handleResize);
    document.addEventListener('keydown', this.handleKeyDown);
  }

  componentDidUpdate(_: {}, prevState: HeaderState) {
    if (this.state.isMenuOpen !== prevState.isMenuOpen) {
      document.body.style.overflow = this.state.isMenuOpen ? 'hidden' : '';
    }
  }

  componentWillUnmount() {
    if (this.handleScroll) window.removeEventListener('scroll', this.handleScroll);
    if (this.handleResize) window.removeEventListener('resize', this.handleResize);
    if (this.handleKeyDown) document.removeEventListener('keydown', this.handleKeyDown);
    document.body.style.overflow = '';
  }

  handleMenuClick = (menuId: string) => {
    this.setState({ activeMenu: menuId, isMenuOpen: false });
  }

  toggleMenu = () => {
    this.setState(prev => ({ isMenuOpen: !prev.isMenuOpen }));
  }

  closeMenu = () => {
    this.setState({ isMenuOpen: false });
  }

  render() {
    const { isScrolled, isMenuOpen, activeMenu } = this.state;

    return (
      <header
        className={`${styles.header} ${isScrolled ? styles.headerScrolled : ''} ${isMenuOpen ? styles.menuOpen : ''}`}
        role="banner"
      >
        <div className={styles.headerContainer}>
          {/* Logo */}
          <div className={styles.headerLogo}>
            <Link to="/" className={styles.logo}>
              <img
                src="/images/chatup24-logo.png"
                alt="Chatup24"
                className={styles.logoImage}
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className={styles.headerNav} role="navigation" aria-label="Main navigation">
            <ul className={styles.navMenu} role="menubar">
              {menuItems.map((item) => (
                <li key={item.id} className={styles.navItem} role="none">
                  <a
                    href={item.href}
                    className={`${styles.navLink} ${activeMenu === item.id ? styles.navLinkActive : ''}`}
                    role="menuitem"
                    aria-current={activeMenu === item.id ? 'page' : undefined}
                    onClick={() => this.handleMenuClick(item.id)}
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {/* Desktop Auth */}
          <div className={styles.headerCta}>
            <Link to="/login" className={styles.loginButton}>
              Login
            </Link>
            <Link to="/signup" className={styles.btnBook}>
              Sign Up
            </Link>
          </div>

          {/* Mobile Hamburger */}
          <button
            className={`${styles.menuToggle} ${isMenuOpen ? styles.isOpen : ''}`}
            onClick={this.toggleMenu}
            aria-label="Toggle menu"
            aria-expanded={isMenuOpen}
            aria-controls="mobile-menu"
          >
            <span className={styles.hamburgerIcon} />
          </button>
        </div>

        {/* Mobile Overlay */}
        {isMenuOpen && (
          <div
            className={styles.mobileMenuOverlay}
            onClick={this.closeMenu}
            aria-hidden="true"
          />
        )}

        {/* Mobile Drawer */}
        <div
          id="mobile-menu"
          className={`${styles.mobileMenu} ${isMenuOpen ? styles.isOpen : ''}`}
          aria-hidden={!isMenuOpen}
          role="navigation"
          aria-label="Mobile navigation"
        >
          <div className={styles.mobileMenuScroll}>
            <ul className={styles.mobileNavMenu} role="menubar">
              {menuItems.map((item) => (
                <li key={item.id} className={styles.mobileNavItem} role="none">
                  <a
                    href={item.href}
                    className={`${styles.mobileNavLink} ${activeMenu === item.id ? styles.mobileNavLinkActive : ''}`}
                    onClick={() => this.handleMenuClick(item.id)}
                    role="menuitem"
                    aria-current={activeMenu === item.id ? 'page' : undefined}
                  >
                    <span className={styles.mobileNavIcon}>{item.icon}</span>
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className={styles.mobileMenuFooter}>
            <Link
              to="/login"
              className={styles.mobileAuthLink}
              onClick={this.closeMenu}
            >
              Login
            </Link>
            <Link
              to="/signup"
              className={styles.mobileAuthLinkPrimary}
              onClick={this.closeMenu}
            >
              Sign Up
            </Link>
          </div>
        </div>
      </header>
    )
  }
}
